import { type CylinderParams, validateCylinder } from '../geometry/cylinder-params';
import { enCylinderizeSize } from '../geometry/cylinder-projection';
import { makeX } from '../geometry/cylinder-x';
import { curveBounds } from '../geometry/cylinder-y';
import type { Rect } from '../geometry/types';
import { sampleMarppleBilinear } from './bilinear-sampler';
import { roundTripOwnedBuffer } from './premul-canvas';
import { createRaster, type Raster, RGBA_CHANNELS } from './raster';

/** 원본 행 판정에서 부동소수 오차로 0 이나 area.height − 1 을 넘은 값을 끝 행으로 보는 허용치(px). */
const ROW_EPSILON = 1e-9;

/**
 * 원통 미리보기. 마플 미리보기 이미지(`cv_preview`)에 대응한다.
 * - raster: 열 수는 투영 폭을 버림한 값이고(마플 실측) 행 수는 투영 높이를 올림한 값이다. 픽셀은 직선 RGBA 다.
 *   마플 표본의 투영 높이는 모두 정수라 소수일 때의 규칙은 근거가 없다. 출력이 투영 영역을 덮도록 올림으로 정했다.
 * - placement: raster 를 그릴 캔버스 사각형. 투영 영역(enCylinderizeSize) 위치이고, 폭은 raster 열 수보다 조금 넓은 소수다.
 */
export interface CylinderPreview {
  readonly raster: Raster;
  readonly placement: Rect;
}

/**
 * 원본 디자인을 원통 면에 감은 미리보기를 마플 렌더 경로대로 만든다.
 * - 출력 열 x 의 가로 비율은 c = x / 열 수(픽셀 왼쪽 모서리 기준)이고, 행 y 는 캔버스 y = 투영 top + y 다.
 * - 원본 y 가 0 ~ area.height − 1 인 픽셀만 그리고 나머지는 투명이다. 경계에 안티앨리어싱은 없다.
 * - 픽셀 값은 마플 샘플러(sampleMarppleBilinear)로 읽고, 끝으로 캔버스 저장처럼 premultiplied 8비트 반올림을 거친다.
 * source 는 area 크기와 같아야 하고, 원본 픽셀 (i, j) 는 area 왼쪽 위에서 (i, j) px 떨어진 점이다.
 * params 가 validateCylinder 를 통과하지 못하거나, 크기가 맞지 않거나, 투영 폭이 1px 보다 좁으면 RangeError 다.
 */
export function warpCylinder(source: Raster, params: CylinderParams): CylinderPreview {
  assertWarpable(source, params);
  const projected = enCylinderizeSize(params);
  const columns = Math.floor(projected.width);
  if (columns < 1) {
    throw new RangeError(`투영 폭 ${projected.width} 가 1px 보다 좁아 그릴 열이 없다`);
  }
  const rows = Math.ceil(projected.height);
  const raw = createRaster(columns, rows);
  const { area } = params;
  const lastRow = area.height - 1;
  for (let x = 0; x < columns; x += 1) {
    const c = x / columns;
    const sourceX = makeX(c, area) - area.left;
    const { top, bottom } = curveBounds(c, params);
    const rowScale = area.height / (bottom - top);
    for (let y = 0; y < rows; y += 1) {
      const sourceY = (projected.top + y - top) * rowScale;
      if (sourceY < -ROW_EPSILON || sourceY > lastRow + ROW_EPSILON) continue;
      const clampedY = Math.min(lastRow, Math.max(0, sourceY));
      sampleMarppleBilinear(source, sourceX, clampedY, raw.data, (y * columns + x) * RGBA_CHANNELS);
    }
  }
  roundTripOwnedBuffer(raw.data);
  return { raster: raw, placement: { ...projected, height: rows } };
}

function assertWarpable(source: Raster, params: CylinderParams): void {
  const issues = validateCylinder(params);
  if (issues.length > 0) {
    throw new RangeError(`원통 파라미터를 쓸 수 없다: ${issues.join(', ')}`);
  }
  const { area } = params;
  if (source.width !== area.width || source.height !== area.height) {
    throw new RangeError(
      `원본 크기 ${source.width}×${source.height} 가 area 크기 ${area.width}×${area.height} 와 다르다`,
    );
  }
}
